import React, { ReactNode } from 'react';
import { Box, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';

interface CustomTabProps {
  icon: ReactNode;
  label: string;
  isActive: boolean;
  onClick: () => void;
}

const TabContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isActive',
})<{ isActive: boolean }>(({ theme, isActive }) => ({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  padding: theme.spacing(1, 2),
  borderRadius: '4px 4px 0px 0px',
  border: '1px solid #CDD6D9',
  backgroundColor: isActive ? '#FFFFFF' : '#F8F8F8',
  color: isActive ? '#1976d2' : '#000000',
  minHeight: '48px',
  cursor: 'pointer',
  borderBottom: isActive ? '1px solid #FFFFFF' : '1px solid #CDD6D9',
  marginBottom: isActive ? '-1px' : 0,
  '&:hover': {
    backgroundColor: isActive ? '#FFFFFF' : '#e0e0e0',
  },
}));

const IconWrapper = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isActive',
})<{ isActive: boolean }>(({ isActive }) => ({
  marginRight: '8px',
  display: 'flex',
  alignItems: 'center',
  color: isActive ? '#1976d2' : '#87949C',
}));

const CustomTab: React.FC<CustomTabProps> = ({ icon, label, isActive, onClick }) => {
  const sanitizedLabel = label.toLowerCase().replace(/\s+/g, '-');
  
  return (
    <TabContainer 
      isActive={isActive} 
      onClick={onClick} 
      role="tab" 
      aria-selected={isActive}
      data-testid={`tab-${sanitizedLabel}`}
    >
      <IconWrapper 
        isActive={isActive}
        data-testid={`tab-icon-${sanitizedLabel}`}
      >
        {icon}
      </IconWrapper>
      <Typography 
        variant="body1" 
        fontWeight={500}
        data-testid={`tab-label-${sanitizedLabel}`}
      >
        {label}
      </Typography>
    </TabContainer>
  );
};

export default CustomTab; 